/**
 * Format a number as percentage
 * @param value Value to format as percentage
 * @param locale Language code (e.g., 'en', 'ar')
 * @param decimals Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercent = (
	value: number,
	locale: string = 'en-US',
	decimals: number = 0
): string => {
	return new Intl.NumberFormat(locale, {
		style: 'percent',
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	}).format(value / 100);
};

/**
 * Format a date to a localized string
 * @param date Date to format
 * @param locale Language code (e.g., 'en', 'ar')
 * @param options DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
	date: Date | string,
	locale: string = 'en',
	options: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	}
): string => {
	const dateObj = date instanceof Date ? date : new Date(date);

	return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

/**
 * Format a number as currency
 * @param amount Amount to format
 * @param locale Language code (e.g., 'en', 'ar')
 * @param currency Currency code (e.g., 'USD', 'EUR')
 * @returns Formatted currency string
 */
export const formatCurrency = (
	amount: number,
	locale: string = 'en-US',
	currency: string = 'USD'
): string => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

/**
 * Format
 */
import type { Currency } from '../types/currency';

/**
 * Convert a base amount using the selected currency's exchange rate
 * Assumes exchange_rate is relative to base: converted = amount * exchange_rate
 */
export const convertPrice = (amount: number, currency?: Currency | null): number => {
	if (!currency) return amount;
	const rate = currency.is_base ? 1 : (currency.exchange_rate ?? 1);
	return amount * rate;
};

/**
 * Convert and format amount using a Currency object
 */
export const formatPriceWithCurrency = (
	amount: number,
	locale: string = 'en-US',
	currency?: Currency | null
): string => {
	const converted = convertPrice(amount, currency);
	const code = currency?.iso || 'USD';
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: code,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(converted);
};